import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatSAR, formatDate, statusColor } from "@/lib/utils";
import TripMap, { CITY_COORDS } from "@/components/TripMap";
import { toast } from "sonner";
import { Phone, MessageSquare, MapPin, Play, CheckCircle2 } from "lucide-react";

export function DriverToday() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/bookings").then((r) => setBookings(r.data.items));
  useEffect(() => { load(); }, []);
  const today = bookings.filter((b) => ["dispatched", "in-progress"].includes(b.status));
  const upcoming = bookings.filter((b) => b.status === "confirmed");
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Today's Trips</h1>
      {today.length === 0 && upcoming.length === 0 && (
        <Card><CardContent className="p-8 text-center text-slate-500">No trips assigned yet.</CardContent></Card>
      )}
      {[...today, ...upcoming].map((b) => (<TripCard key={b.id} b={b} onUpdate={load} />))}
    </div>
  );
}

function TripCard({ b, onUpdate }) {
  const update = async (status) => {
    try { await api.patch(`/bookings/${b.id}/status`, { status }); toast.success(`Status: ${status}`); onUpdate?.(); }
    catch { toast.error("Update failed"); }
  };
  const sendLoc = async () => {
    const o = CITY_COORDS[b.origin], d = CITY_COORDS[b.destination];
    if (!o || !d) return;
    const lat = (o[0] + d[0]) / 2 + (Math.random() - 0.5) * 0.3;
    const lng = (o[1] + d[1]) / 2 + (Math.random() - 0.5) * 0.3;
    await api.patch(`/bookings/${b.id}/location`, { lat, lng });
    toast.success("Location updated");
    onUpdate?.();
  };
  return (
    <Card data-testid={`trip-card-${b.code}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><div className="text-xs uppercase text-slate-500 tracking-widest">Trip Code</div><div className="font-mono font-bold text-xl">{b.code}</div></div>
          <Badge className={statusColor(b.status)}>{b.status}</Badge>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div><div className="text-xs text-slate-500 uppercase">From</div><div className="font-semibold">{b.origin}</div></div>
          <div><div className="text-xs text-slate-500 uppercase">To</div><div className="font-semibold">{b.destination}</div></div>
          <div><div className="text-xs text-slate-500 uppercase">Pickup</div><div className="font-semibold text-sm">{formatDate(b.pickup_date)}</div></div>
          <div><div className="text-xs text-slate-500 uppercase">Passengers</div><div className="font-semibold">{b.passengers}</div></div>
        </div>
        <div className="rounded-xl bg-slate-50 p-4 flex flex-wrap items-center justify-between gap-3">
          <div><div className="text-xs text-slate-500 uppercase">Passenger</div><div className="font-semibold">{b.customer_name}</div><div className="text-sm text-slate-500">{b.customer_phone}</div></div>
          <div className="flex gap-2">
            <a href={`tel:${b.customer_phone}`}><Button size="sm" variant="outline" data-testid={`call-${b.code}`}><Phone className="w-4 h-4 mr-1" />Call</Button></a>
            <a href={`https://wa.me/${b.customer_phone?.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid={`wa-${b.code}`}><MessageSquare className="w-4 h-4 mr-1" />WhatsApp</Button></a>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {b.status === "dispatched" && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => update("in-progress")} data-testid={`start-${b.code}`}><Play className="w-4 h-4 mr-1" />Start Trip</Button>}
          {b.status === "in-progress" && <>
            <Button variant="outline" onClick={sendLoc} data-testid={`loc-${b.code}`}><MapPin className="w-4 h-4 mr-1" />Update Location</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => update("completed")} data-testid={`done-${b.code}`}><CheckCircle2 className="w-4 h-4 mr-1" />Complete</Button>
          </>}
        </div>
      </CardContent>
    </Card>
  );
}

export function DriverNavigation() {
  const [bookings, setBookings] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/bookings").then((r) => { setBookings(r.data.items); setSel(r.data.items.find((b) => b.status === "in-progress") || r.data.items[0]); }); }, []);
  if (!sel) return <div className="text-slate-500">No active trip.</div>;
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Navigation</h1>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2"><CardContent className="p-4"><TripMap origin={sel.origin} destination={sel.destination} current={sel.current_location} height={520} /></CardContent></Card>
        <div className="space-y-2">
          {bookings.filter((b) => ["dispatched", "in-progress"].includes(b.status)).map((b) => (
            <button key={b.id} onClick={() => setSel(b)} data-testid={`nav-trip-${b.code}`}
              className={`w-full text-left abdan-card p-4 ${sel.id === b.id ? "ring-2 ring-emerald-500" : ""}`}>
              <div className="font-mono font-bold">{b.code}</div>
              <div className="text-sm text-slate-600">{b.origin} → {b.destination}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DriverHistory() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => setRows(r.data.items.filter((b) => b.status === "completed"))); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Trip History</h1>
      {rows.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">No completed trips.</CardContent></Card>}
      {rows.map((b) => (
        <Card key={b.id}><CardContent className="p-4 flex items-center justify-between">
          <div><div className="font-mono font-bold">{b.code}</div><div className="text-sm text-slate-500">{b.origin} → {b.destination} · {formatDate(b.pickup_date)}</div></div>
          <div className="text-right"><Badge className={statusColor(b.status)}>{b.status}</Badge><div className="font-semibold text-emerald-700 mt-1">{formatSAR(b.price)}</div></div>
        </CardContent></Card>
      ))}
    </div>
  );
}
